import { useRef, useState, useEffect } from "react";

/**
 * Computes a DOM property from an element. Assign `ref={elementRef}` to the node to examine,
 * and its value will be returned in `domProperty`.
 *
 * @param {any[]} deps Array of dependencies, just like in useEffect
 * @param {(el: TElement) => TResult} getDOMProperty A function that takes the ref target and returns the property
 * @param {any} defaultValue The default value to return before the element is rendered
 * @returns {{elementRef: React.MutableRefObject<TElement>, domProperty: TResult}} returns the ref to assign to the element, and the value of the DOM property requested.
 * @template TElement
 * @template TResult
 */
const useElementDOMProperty = ({ getDOMProperty, defaultValue }, deps) => {
  const elementRef = useRef(null);
  const [domProperty, setDomProperty] = useState(defaultValue);
  useEffect(() => {
    if (elementRef.current) {
      setDomProperty(getDOMProperty(elementRef.current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { elementRef, domProperty };
};

export default useElementDOMProperty;
